import type { RefObject } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import {
  findMaxVisibleIndexes,
  parseCssPx,
} from './overflow-pill-rows.layout.utils';

export interface UseOverflowPillLayoutOptions {
  gap: string;
  itemCount: number;
  maxRows: number;
  reserveControlSlot: boolean;
}

export interface UseOverflowPillLayoutResult {
  hasMeasured: boolean;
  isMeasuring: boolean;
  measureRef: RefObject<HTMLDivElement | null>;
  remeasure: () => void;
  trackRef: RefObject<HTMLDivElement | null>;
  visibleItemIndexes: number[];
}

const indexesEqual = (left: number[], right: number[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
};

export const useOverflowPillLayout = ({
  gap,
  itemCount,
  maxRows,
  reserveControlSlot,
}: UseOverflowPillLayoutOptions): UseOverflowPillLayoutResult => {
  const trackRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleItemIndexes, setVisibleItemIndexes] = useState<number[]>([]);
  const [hasMeasured, setHasMeasured] = useState(false);
  const [measureGeneration, setMeasureGeneration] = useState(0);
  const pillWidthsRef = useRef<number[]>([]);
  const controlWidthsRef = useRef<Map<number, number>>(new Map());
  const trackWidthRef = useRef(0);
  const gapPx = parseCssPx(gap);

  const computeLayout = useCallback((): void => {
    if (itemCount === 0) {
      setVisibleItemIndexes(previous =>
        previous.length === 0 ? previous : [],
      );
      setHasMeasured(previous => (previous ? previous : true));
      return;
    }

    const track = trackRef.current;
    const measureTrack = measureRef.current;

    if (!track || !measureTrack) {
      return;
    }

    const trackWidth = track.getBoundingClientRect().width;

    if (trackWidth <= 0) {
      return;
    }

    trackWidthRef.current = trackWidth;

    const pillElements = measureTrack.querySelectorAll(
      '[data-overflow-pill-index]',
    );
    const pillWidths = Array.from(pillElements).map(
      element => element.getBoundingClientRect().width,
    );

    if (pillWidths.length !== itemCount) {
      return;
    }

    pillWidthsRef.current = pillWidths;

    const controlMeasureElements = measureTrack.querySelectorAll(
      '[data-overflow-control-measure]',
    );

    controlMeasureElements.forEach(element => {
      if (!(element instanceof HTMLElement)) {
        return;
      }

      const hiddenCount = Number.parseInt(
        element.dataset.hiddenCount ?? '1',
        10,
      );

      controlWidthsRef.current.set(
        hiddenCount,
        element.getBoundingClientRect().width,
      );
    });

    const getControlWidth = (hiddenCount: number): number => {
      const cached = controlWidthsRef.current.get(hiddenCount);

      if (cached !== undefined) {
        return cached;
      }

      const fallback = controlWidthsRef.current.get(itemCount);

      if (fallback !== undefined) {
        return fallback;
      }

      const firstWidth = controlWidthsRef.current.values().next().value;

      return firstWidth ?? 40;
    };

    const nextVisible = findMaxVisibleIndexes({
      gap: gapPx,
      getControlWidth,
      itemCount,
      maxRows,
      pillWidths,
      reserveControlSlot,
      trackWidth,
    });

    setVisibleItemIndexes(previous =>
      indexesEqual(previous, nextVisible) ? previous : nextVisible,
    );
    setHasMeasured(true);
  }, [gapPx, itemCount, maxRows, reserveControlSlot]);

  const recalculateFromCache = useCallback((): void => {
    if (itemCount === 0 || pillWidthsRef.current.length !== itemCount) {
      computeLayout();
      return;
    }

    const trackWidth = trackRef.current?.getBoundingClientRect().width ?? 0;

    if (trackWidth <= 0) {
      return;
    }

    trackWidthRef.current = trackWidth;

    const getControlWidth = (hiddenCount: number): number => {
      const cached = controlWidthsRef.current.get(hiddenCount);

      if (cached !== undefined) {
        return cached;
      }

      const fallback = controlWidthsRef.current.get(itemCount);

      if (fallback !== undefined) {
        return fallback;
      }

      const firstWidth = controlWidthsRef.current.values().next().value;

      return firstWidth ?? 40;
    };

    const nextVisible = findMaxVisibleIndexes({
      gap: gapPx,
      getControlWidth,
      itemCount,
      maxRows,
      pillWidths: pillWidthsRef.current,
      reserveControlSlot,
      trackWidth,
    });

    setVisibleItemIndexes(previous =>
      indexesEqual(previous, nextVisible) ? previous : nextVisible,
    );
    setHasMeasured(true);
  }, [computeLayout, gapPx, itemCount, maxRows, reserveControlSlot]);

  const remeasure = useCallback(() => {
    pillWidthsRef.current = [];
    controlWidthsRef.current = new Map();
    setHasMeasured(false);
    setMeasureGeneration(previous => previous + 1);
  }, []);

  useLayoutEffect(() => {
    pillWidthsRef.current = [];
    controlWidthsRef.current = new Map();
    setHasMeasured(false);
    setMeasureGeneration(previous => previous + 1);
  }, [gap, itemCount, maxRows, reserveControlSlot]);

  useLayoutEffect(() => {
    computeLayout();

    if (!hasMeasured && itemCount > 0) {
      const frameId = requestAnimationFrame(() => {
        computeLayout();
      });

      return () => {
        cancelAnimationFrame(frameId);
      };
    }

    return undefined;
  }, [computeLayout, hasMeasured, itemCount, measureGeneration]);

  useLayoutEffect(() => {
    const track = trackRef.current;

    if (!track || itemCount === 0) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      const nextWidth = entry.contentRect.width;

      if (Math.abs(nextWidth - trackWidthRef.current) < 1) {
        return;
      }

      recalculateFromCache();
    });

    resizeObserver.observe(track);

    return () => {
      resizeObserver.disconnect();
    };
  }, [itemCount, recalculateFromCache]);

  return {
    hasMeasured,
    isMeasuring: !hasMeasured && itemCount > 0,
    measureRef,
    remeasure,
    trackRef,
    visibleItemIndexes,
  };
};
